import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {CampsiteFormProps, FormType} from './types';
import Step1 from '../shared/CustomerFormSteps/Step1';
import {Step1Props} from '../shared/CustomerFormSteps/Step1/types';
import Step2 from './Step2';
import {Step2Props} from './Step2/types';
import Step3 from './Step3';
import {Step3Props} from './Step3/types';
import Step4 from '../shared/CustomerFormSteps/Step4';
import {Step4Props} from '../shared/CustomerFormSteps/Step4/types';
import Step5 from '../shared/CustomerFormSteps/Step5';
import {Step5Props} from '../shared/CustomerFormSteps/Step5/types';
import {
    Step1ValueModel,
    Step4ValueModel,
    Step5ValueModel,
} from '../shared/CustomerFormSteps/types';
import {Step2ValueModel, Step3ValueModel} from './types';
import {
    getSubmitCampsiteRegistrationBody,
    getSubmitSkiResortRegistrationBody,
    getSubmitWorkPlaceRegistrationBody,
    getSections,
} from './utils';
import {
    fetchMunicipalities,
    fetchCampsiteHeards,
    fetchSkiResortHeards,
    fetchWorkPlaceHeards,
} from '../shared/utils';
import CompanyRegistrationsService from "@apps/Shared/Services/CompanyRegistrations/CompanyRegistrations.service";
import {StatusCode} from "@apps/Shared/Services/Response.types";
import {IHeard, IMunicipality} from "@apps/Shared/Services/CompanyRegistrations/interfaces";
import PantaForm from "@sharedComponents/PantaForm";
import PantaFormStep from "@sharedComponents/PantaForm/PantaFormStep";
import Help from "@sharedComponents/Help";

function submit(
    formType: FormType,
    myPagesRoute: string,
    setSubmitting: Function,
    setRequestErrors: Function,
    setUnexpectedErrors: Function,
    values: Step1ValueModel &
        Step2ValueModel &
        Step3ValueModel &
        Step4ValueModel &
        Step5ValueModel
) {
    const getBodySwitch = (formType: FormType) => {
        switch (formType) {
            case FormType.Campsite:
                return getSubmitCampsiteRegistrationBody
            case FormType.SkiResort:
                return getSubmitSkiResortRegistrationBody
            case FormType.WorkPlace:
                return getSubmitWorkPlaceRegistrationBody
            default:
                return getSubmitCampsiteRegistrationBody
        }
    }
    const getBody = getBodySwitch(formType)

    const submitRegistrationSwitch = (formType: FormType) => {
        switch (formType) {
            case FormType.Campsite:
                return CompanyRegistrationsService.SubmitCampsiteRegistration
            case FormType.SkiResort:
                return CompanyRegistrationsService.SubmitSkiResortRegistration
            case FormType.WorkPlace:
                return CompanyRegistrationsService.SubmitWorkPlaceRegistration
            default:
                return CompanyRegistrationsService.SubmitCampsiteRegistration
        }
    }
    const submitRegistration = submitRegistrationSwitch(formType)

    const body = getBody(values)

    setSubmitting(true)
    setUnexpectedErrors([])

    submitRegistration(body)
        .then(result => {
            if (result.status === StatusCode.OK) {
                if (window.location.pathname === myPagesRoute) {
                    window.location.reload()
                } else {
                    window.location.href = myPagesRoute
                }
            } else if (result.status === StatusCode.BAD_REQUEST) {
                if (result.data.message) {
                    setRequestErrors(result.data.message)
                } else {
                    setRequestErrors({})
                }

                setSubmitting(false)
            } else {
                const errorMessages = result.data.message

                setUnexpectedErrors(errorMessages || [])

                setSubmitting(false)
            }
        })
        .catch((error: any) => {
            console.log(error)

            setSubmitting(false)
        })
}

function resetForm(setInitialValues: Function) {
    setInitialValues({})
}

function resetRequestErrors(setRequestErrors: Function) {
    setRequestErrors({})
}

const CampsiteForm = (props: CampsiteFormProps) => {
    const {t} = useTranslation()

    const [rehydrateForm] = useState<boolean>(false)
    const [initialStep] = useState<number>(1)
    const [initialValues, setInitialValues] = useState<any>({})
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [requestErrors, setRequestErrors] = useState({})
    const [unexpectedErrors, setUnexpectedErrors] = useState<string[]>([])
    const [municipalities, setMunicipalities] = useState<IMunicipality[]>([])
    const [heards, setHeards] = useState<IHeard[]>([])

    useEffect(() => {
        fetchMunicipalities(setMunicipalities, setUnexpectedErrors)

        if (props.formType === FormType.Campsite) {
            fetchCampsiteHeards(setHeards, setUnexpectedErrors)
        }

        if (props.formType === FormType.SkiResort) {
            fetchSkiResortHeards(setHeards, setUnexpectedErrors)
        }

        if (props.formType === FormType.WorkPlace) {
            fetchWorkPlaceHeards(setHeards, setUnexpectedErrors)
        }
    }, [])

    if (
        props.formType !== FormType.Campsite &&
        props.formType !== FormType.SkiResort &&
        props.formType !== FormType.WorkPlace
    ) {
        return <></>
    }

    const tPrefix = props.formType?.toString()

    return (
        <div>
            <Help
                content={props.helpText}
                fullscreen={true}
            />
            <div className='form-wrapper'>
                <PantaForm
                    onSubmit={submit.bind(
                        null,
                        props.formType,
                        props.myPagesRoute,
                        setSubmitting,
                        setRequestErrors,
                        setUnexpectedErrors
                    )}
                    onCancel={props.onCancel}
                    defaultValues={initialValues}
                    initialStep={initialStep}
                >
                    <PantaFormStep
                        component={Step1}
                        stepProps={
                            {
                                heading: t(
                                    `companyRegistration.customer.${tPrefix}.stepLabel.step1`
                                ),
                                cmsContent: props.cmsContent.step1,
                                rehydrateFormKey: rehydrateForm,
                            } as Step1Props
                        }
                    />
                    <PantaFormStep
                        component={Step2}
                        stepProps={
                            {
                                cmsContent: props.cmsContent.step2,
                                rehydrateFormKey: rehydrateForm,
                                newRegistration: resetForm.bind(
                                    null,
                                    setInitialValues
                                ),
                                tPrefix,
                            } as unknown as Step2Props // TODO: varför måste vi göra såhär?
                        }
                    />
                    <PantaFormStep
                        component={Step3}
                        stepProps={
                            {
                                cmsContent: props.cmsContent.step3,
                                rehydrateFormKey: rehydrateForm,
                                municipalities: municipalities,
                                heards: heards,
                                unexpectedErrors: unexpectedErrors,
                                tPrefix,
                            } as Step3Props
                        }
                    />
                    <PantaFormStep
                        component={Step4}
                        stepProps={
                            {
                                heading: t(
                                    `companyRegistration.customer.${tPrefix}.stepLabel.step4`
                                ),
                                cmsContent: props.cmsContent.step4,
                                rehydrateFormKey: rehydrateForm,
                                hideReportPersonForm: true,
                            } as Step4Props
                        }
                    />
                    <PantaFormStep
                        component={Step5}
                        stepProps={
                            {
                                heading: t(
                                    `companyRegistration.customer.${tPrefix}.stepLabel.step5`
                                ),
                                cmsContent: props.cmsContent.step5,
                                rehydrateFormKey: rehydrateForm,
                                termsAndConditions: props.termsAndConditions,
                                termsAndConditionsButtons: props.termsAndConditionsButtons,
                                submitting: submitting,
                                requestErrors: requestErrors,
                                resetRequestErrors: resetRequestErrors.bind(
                                    null,
                                    setRequestErrors
                                ),
                                unexpectedErrors: unexpectedErrors,
                                getSections: getSections.bind(null, t, tPrefix),
                            } as Step5Props
                        }
                    />
                </PantaForm>
            </div>
        </div>
    )
}

export default CampsiteForm